import { api } from "aldoo-ra/API"

const devOpsAPI = api("aldoo-devops")
const taskAPI = api("aldoo-task")

// Default timeout and polling intervals (in milliseconds)
const DEFAULT_TIMEOUT = 5 * 60 * 1000 // 5 minutes
const DEFAULT_POLL_INTERVAL = 2000 // 2 seconds

// Operation specific timeouts and polling intervals
const OPERATION_CONFIG = {
  LIST: {
    timeout: 5 * 60 * 1000, // 5 minutes
    pollInterval: 1000, // 2 seconds
  },
  RESTORE: {
    timeout: 30 * 60 * 1000, // 30 minutes
    pollInterval: 1000, // 5 seconds
  },
  BACKUP: {
    timeout: 20 * 60 * 1000, // 20 minutes
    pollInterval: 5000, // 5 seconds
  },
  SCRIPT: {
    timeout: 10 * 60 * 1000, // 10 minutes
    pollInterval: 2000, // 2 seconds
  },
  UPLOAD: {
    timeout: 15 * 60 * 1000, // 15 minutes
    pollInterval: 2000, // 3 seconds
  },
  READ_GLOBAL: {
    timeout: 2 * 60 * 1000, // 2 minutes
    pollInterval: 500, // 2 seconds
  },
  DOWNLOAD: {
    timeout: 5 * 60 * 1000, // 5 minutes
    pollInterval: 2000, // 2 seconds
  },
}

/**
 * Polls task status until completion or timeout
 * @param {object} response - Initial API response
 * @param {number} timeout - Timeout in milliseconds
 * @param {number} pollInterval - Polling interval in milliseconds
 * @returns {Promise<object>} - Task status result or error
 */
const pollTaskStatus = async ({
  response,
  timeout = DEFAULT_TIMEOUT,
  pollInterval = DEFAULT_POLL_INTERVAL,
  //when a response is received, pass the result to the onStep function
  onResponse,
}) => {
  // Return immediately if there's an error or no task ID
  if (response.error || !response.task) {
    return response
  }

  const startTime = Date.now()
  const taskId = response.task

  while (true) {
    // Check if we've exceeded the timeout
    if (Date.now() - startTime > timeout) {
      return { error: "timeout" }
    }

    // Get task status
    const response = await GetTaskStatus({
      id: taskId,
      result: true,
      log: true,
    })

    if (onResponse) onResponse(response)

    // If there's an error getting status, return it
    if (response.error) return response

    // If task is completed or failed, return the status
    if (
      response.status === "completed" ||
      response.status === "failed" ||
      response.status === "error"
    ) {
      return response
    }

    // Wait for the polling interval
    await new Promise((resolve) => setTimeout(resolve, pollInterval))
  }
}

/**
 * Sample Method
 * @param {*} get - object with the following fields:
 * name - if specified, the method will search the task by name
 * id - if specified, the method will search the task by id
 * result - if true, will include the result field in the response
 * log - if true, will include the log field in the response
 * @returns
 */
export const GetTaskStatus = async (get) => {
  return await taskAPI({ get })
}

/**
 * list is an object with:
 * - suspended: boolean - if true, will include suspended tasks in the list
 * @param {*} list
 * @returns
 */
export const GetTaskList = async (list) => {
  return await taskAPI({ list })
}

//stop task by name or id via stop object
export const StopTask = async (stop) => {
  return await taskAPI({ stop })
}

/**
 * Restore backup with task polling
 * @param {*} run - Run configuration object
 * @param {number} [timeout] - Optional custom timeout
 * @returns {Promise<object>} - Task result or error
 */
export const RestoreBackup = async ({
  run,
  timeout = OPERATION_CONFIG.RESTORE.timeout,
  //on every response pass the result to the onResponse function
  onResponse,
}) => {
  const response = await devOpsAPI({ run })
  return await pollTaskStatus({
    response,
    timeout,
    pollInterval: OPERATION_CONFIG.RESTORE.pollInterval,
    onResponse,
  })
}

/**
 * Create backup with task polling
 * @param {*} run - Run configuration object
 * @param {number} [timeout] - Optional custom timeout
 * @returns {Promise<object>} - Task result or error
 */
export const CreateBackup = async ({
  run,
  timeout = OPERATION_CONFIG.BACKUP.timeout,
  //on every response pass the result to the onResponse function
  onResponse,
}) => {
  const response = await devOpsAPI({ run })
  return await pollTaskStatus({
    response,
    timeout,
    pollInterval: OPERATION_CONFIG.BACKUP.pollInterval,
    onResponse,
  })
}

/**
 * List backups
 * @param {*} list - List configuration object
 * @returns {Promise<object>} - List result
 */
export const ListBackups = async (
  run,
  timeout = OPERATION_CONFIG.LIST.timeout
) => {
  await new Promise((resolve) => setTimeout(resolve, 100))

  const response = await devOpsAPI({ run })

  return await pollTaskStatus({
    response,
    timeout,
    pollInterval: OPERATION_CONFIG.LIST.pollInterval,
  })
}

/**
 * Upload backup with task polling
 * @param {*} formData - Form data with file
 * @param {Function} setProgress - Progress callback
 * @param {number} [timeout] - Optional custom timeout
 * @returns {Promise<object>} - Task result or error
 */
export const UploadBackup = async ({
  //file object
  file,
  setProgress,
  timeout = OPERATION_CONFIG.UPLOAD.timeout,
}) => {
  // Add the command field
  const formData = new FormData()
  formData.append("file", file)
  formData.append("run", 1)
  formData.append("script", "backup-upload")

  const response = await devOpsAPI(formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round(
        (progressEvent.loaded / progressEvent.total) * 100
      )
      setProgress(progress)
    },
  })

  return await pollTaskStatus({
    response,
    timeout,
    pollInterval: OPERATION_CONFIG.UPLOAD.pollInterval,
  })
}

//DeleteBackup
export const DeleteBackup = async (run) => {
  return await devOpsAPI({ run })
}

//DownloadBackup
export const DownloadBackup = async (imageName, isLocal) => {
  const response = await devOpsAPI({
    run: {
      script: "backup-download",
      env: {
        BACKUP_IMAGE_FILE: imageName,
        IS_LOCAL: isLocal,
      },
    },
  })
  const taskResponse = await pollTaskStatus({
    response,
    timeout: OPERATION_CONFIG.READ_GLOBAL.timeout,
    pollInterval: OPERATION_CONFIG.READ_GLOBAL.pollInterval,
  })

  //return the full response if there's an error
  if (taskResponse.error) {
    return taskResponse
  }

  return taskResponse.result
}

/**
 * Run script with task polling
 * @param {*} run - Run configuration object
 * @param {number} [timeout] - Optional custom timeout
 * @returns {Promise<object>} - Task result or error
 */
export const RunScript = async (
  run,
  timeout = OPERATION_CONFIG.SCRIPT.timeout
) => {
  const response = await devOpsAPI({ run })
  return await pollTaskStatus({
    response,
    timeout,
    pollInterval: OPERATION_CONFIG.SCRIPT.pollInterval,
  })
}

export const ReadGlobal = async (KEY) => {
  const response = await devOpsAPI({
    run: {
      script: "read-global-storage",
      env: { KEY },
    },
  })
  const taskResponse = await pollTaskStatus({
    response,
    timeout: OPERATION_CONFIG.READ_GLOBAL.timeout,
    pollInterval: OPERATION_CONFIG.READ_GLOBAL.pollInterval,
  })

  //return the full response if there's an error
  if (taskResponse.error) {
    return taskResponse
  }

  return taskResponse.result
}

export default {
  GetTaskStatus,
  GetTaskList,
  StopTask,
  RestoreBackup,
  CreateBackup,
  ListBackups,
  UploadBackup,
  DeleteBackup,
  RunScript,
  ReadGlobal,
  DownloadBackup,
}
